import React, { useState, useEffect } from 'react';
import { Trash2, RefreshCw, AlertCircle } from 'lucide-react';
import { clearCachesAndReload, isInReloadLoop } from '../utils/navigation';

/**
 * Development helper component to clear caches and fix reload loops
 * Only shows in development or when there's a reload loop detected
 */
const CacheCleaner: React.FC = () => {
  const [show, setShow] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [inLoop, setInLoop] = useState(false);

  useEffect(() => {
    // Show if in development or reload loop detected
    const isDev = process.env.NODE_ENV === 'development';
    const hasLoop = isInReloadLoop();
    
    setInLoop(hasLoop);
    setShow(isDev || hasLoop);
  }, []);

  const handleClearCache = async () => {
    setIsClearing(true);
    await clearCachesAndReload();
  };

  const handleDismiss = () => {
    setShow(false);
    sessionStorage.setItem('cache-cleaner-dismissed', 'true');
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-4 left-4 z-50 max-w-md">
      {inLoop ? (
        // Critical reload loop warning
        <div className="bg-red-500 text-white rounded-lg shadow-2xl p-4 border-2 border-red-700">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-6 w-6 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-bold text-lg mb-1">Reload Loop Detected!</h3>
              <p className="text-sm mb-3">
                The app is stuck in an infinite reload loop. Clear the cache to fix this.
              </p>
              <button
                onClick={handleClearCache}
                disabled={isClearing}
                className="bg-white text-red-600 px-4 py-2 rounded-lg font-semibold hover:bg-red-50 transition-colors disabled:opacity-50 flex items-center space-x-2"
              >
                {isClearing ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    <span>Clearing...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    <span>Clear Cache & Reload</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      ) : (
        // Development cache cleaner
        <div className="bg-blue-500 text-white rounded-lg shadow-xl p-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold text-sm">Dev Tools</h4>
            <button
              onClick={handleDismiss}
              className="text-white/80 hover:text-white text-xs"
            >
              ✕
            </button>
          </div>
          <button
            onClick={handleClearCache}
            disabled={isClearing}
            className="bg-white text-blue-600 px-3 py-1.5 rounded text-sm font-medium hover:bg-blue-50 transition-colors disabled:opacity-50 flex items-center space-x-2 w-full justify-center"
          >
            {isClearing ? (
              <>
                <RefreshCw className="h-3 w-3 animate-spin" />
                <span>Clearing...</span>
              </>
            ) : (
              <>
                <Trash2 className="h-3 w-3" />
                <span>Clear Cache</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default CacheCleaner;
